/**
 * Auto Member Discount
 * --------------------
 * Automatically applies the "PRIVATEMEMBER20" discount code to the cart when
 * the customer is NOT a private member (does not have the
 * "amy-ann-living-private-members_0" tag) AND a specific product
 * (variant id 15431244775799) is in the cart.
 *
 * If the trigger product is removed from the cart, the discount is removed too.
 */

import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';

const TRIGGER_VARIANT_ID = 15431244775799;
const DISCOUNT_CODE = 'PRIVATEMEMBER20';
const MEMBER_TAG = 'amy-ann-living-private-members_0';

let isSyncing = false;

/**
 * Returns true if the currently logged in customer already is a private member.
 * Customers without an account are treated as non-members.
 */
function customerIsMember() {
  const customer = (typeof Theme !== 'undefined' && Theme && Theme.customer) || null;
  if (!customer || !Array.isArray(customer.tags)) return false;
  return customer.tags.some((tag) => String(tag).toLowerCase() === MEMBER_TAG.toLowerCase());
}

/**
 * Checks whether the trigger product (id 15431244775799) is in the cart.
 * Matches against both variant_id and product_id so it works regardless of
 * whether the supplied id refers to a product or a variant.
 * @param {{items?: Array<{id?: number, variant_id?: number, product_id?: number}>}} cart
 */
function cartContainsTriggerProduct(cart) {
  if (!cart || !Array.isArray(cart.items)) return false;
  return cart.items.some((item) => {
    const variantId = Number(item.variant_id ?? item.id);
    const productId = Number(item.product_id);
    return variantId === TRIGGER_VARIANT_ID || productId === TRIGGER_VARIANT_ID;
  });
}

/**
 * Returns the codes currently applied to the cart, as strings.
 * @param {{discount_codes?: Array<{code: string, applicable?: boolean}>}} cart
 * @returns {string[]}
 */
function getAppliedDiscountCodes(cart) {
  if (!cart || !Array.isArray(cart.discount_codes)) return [];
  return cart.discount_codes
    .filter((d) => d && typeof d.code === 'string' && d.code.length > 0)
    .map((d) => d.code);
}

/**
 * Fetches the current cart object from Shopify.
 * @returns {Promise<any>}
 */
async function fetchCart() {
  const url = (Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
  const response = await fetch(`${url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return await response.json();
}

/**
 * Updates the cart's discount codes.
 * @param {string[]} codes - The full set of discount codes to keep on the cart.
 * @returns {Promise<any>}
 */
async function updateCartDiscounts(codes) {
  const url = (Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update';
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ discount: codes.join(',') }),
  });
  return await response.json();
}

/**
 * Synchronizes the auto-applied discount with the current cart state.
 * Safe to call repeatedly - only performs a network update when the
 * desired state differs from the current state.
 */
async function syncMemberDiscount() {
  if (isSyncing) return;
  isSyncing = true;

  try {
    const cart = await fetchCart();
    const appliedCodes = getAppliedDiscountCodes(cart);
    const hasOurCode = appliedCodes.some(
      (code) => code.toUpperCase() === DISCOUNT_CODE.toUpperCase()
    );
    const hasTrigger = cartContainsTriggerProduct(cart);
    const isMember = customerIsMember();

    const shouldHaveCode = !isMember && hasTrigger;

    if (shouldHaveCode === hasOurCode) return;

    let nextCodes;
    if (shouldHaveCode) {
      nextCodes = [...appliedCodes, DISCOUNT_CODE];
    } else {
      nextCodes = appliedCodes.filter(
        (code) => code.toUpperCase() !== DISCOUNT_CODE.toUpperCase()
      );
    }

    const updatedCart = await updateCartDiscounts(nextCodes);

    document.dispatchEvent(new DiscountUpdateEvent(updatedCart, 'auto-member-discount'));
  } catch (error) {
    console.warn('[auto-member-discount] sync failed:', error);
  } finally {
    isSyncing = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  syncMemberDiscount();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncMemberDiscount());
} else {
  syncMemberDiscount();
}
